import { useEffect, useState } from 'react'

/**
 * A scroll box and whether it hides more content past its end — below (`y`, the rail's list)
 * or to the right (`x`, a row of tabs on a phone) — for the `.fade-bottom` / `.fade-end` masks
 * (design §6.6, §6.15): «there is more» reads without a scrollbar. Re-measured on scroll and
 * whenever the box or its children change size.
 */
export function useMore<T extends HTMLElement>(axis: 'x' | 'y'): [(el: T | null) => void, boolean] {
  const [el, setEl] = useState<T | null>(null)
  const [more, setMore] = useState(false)
  useEffect(() => {
    if (!el) return
    const measure = () =>
      setMore(
        axis === 'y'
          ? el.scrollTop + el.clientHeight < el.scrollHeight - 1
          : el.scrollLeft + el.clientWidth < el.scrollWidth - 1,
      )
    measure()
    el.addEventListener('scroll', measure, { passive: true })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(el)
    for (const child of Array.from(el.children)) observer?.observe(child)
    window.addEventListener('resize', measure)
    return () => {
      el.removeEventListener('scroll', measure)
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [el, axis])
  return [setEl, more]
}
